// NGAP / AMO Maroc — codes & tarifs forfaitaires
// Source: Convention Nationale CNOM / OG — Juillet 2006 + Avenants ANAM
// Organisé par "grille" (≈ spécialité / domaine d'actes).

export type NgapCode = {
  code: string;
  label: string;
  tarif: number; // DHS
  unit?: string; // "forfait", "par séance", "par jour", "par 10K", etc.
  note?: string;
};

export type NgapSpeciality = {
  id: string;
  label: string;        // titre court (utilisé dans les tabs)
  grille: string;       // ex: "Grille 1bis", "Grille 2", "Grille 8"
  scope: string;        // description courte
  unite: string;        // unité par défaut (ex: "forfait global")
  defaultDoctorSpecs: string[]; // spécialités médecins éligibles
  codes: NgapCode[];
};

export const NGAP_SPECIALITIES: NgapSpeciality[] = [
  {
    id: "consultation-cardio",
    label: "Cardiologie · Consultations",
    grille: "Grille 1 bis",
    scope: "Consultation cardiologique et électrocardiogramme",
    unite: "Forfait par acte",
    defaultDoctorSpecs: ["Cardiologie"],
    codes: [
      { code: "CC-ECG", label: "Consultation cardiologie + ECG", tarif: 250, unit: "forfait" },
      { code: "ECG", label: "Électrocardiogramme (ECG)", tarif: 100, unit: "forfait" },
    ],
  },
  {
    id: "chir-hospit",
    label: "Chirurgie · Hospitalisation",
    grille: "Grille 2",
    scope: "Forfaits chirurgicaux par tranche de cotation K",
    unite: "Forfait global / séjour",
    defaultDoctorSpecs: ["Chirurgie générale", "Chirurgie pédiatrique", "Chirurgie Thoracique"],
    codes: [
      { code: "K30-K39", label: "Chirurgie · cotation K30-K39", tarif: 1950 },
      { code: "K40-K49", label: "Chirurgie · cotation K40-K49", tarif: 2600 },
      { code: "K50-K59", label: "Chirurgie · cotation K50-K59", tarif: 4000 },
      { code: "K60-K69", label: "Chirurgie · cotation K60-K69", tarif: 4800 },
      { code: "K70-K79", label: "Chirurgie · cotation K70-K79", tarif: 5600 },
      { code: "K80-K89", label: "Chirurgie · cotation K80-K89", tarif: 6400 },
      { code: "K90-K99", label: "Chirurgie · cotation K90-K99", tarif: 7200 },
      { code: "K100-K109", label: "Chirurgie · K100-K109", tarif: 8000 },
      { code: "K110-K119", label: "Chirurgie · K110-K119", tarif: 8800 },
      { code: "K120-K129", label: "Chirurgie · K120-K129", tarif: 9600 },
      { code: "K130-K139", label: "Chirurgie · K130-K139", tarif: 10400 },
      { code: "K140-K149", label: "Chirurgie · K140-K149", tarif: 11200 },
      { code: "K150-K159", label: "Chirurgie · K150-K159", tarif: 12000 },
      { code: "K160-K169", label: "Chirurgie · K160-K169", tarif: 12800 },
      { code: "K170-K179", label: "Chirurgie · K170-K179", tarif: 13600 },
      { code: "K180-K189", label: "Chirurgie · K180-K189", tarif: 14400 },
      { code: "K190-K199", label: "Chirurgie · K190-K199", tarif: 15200 },
      { code: "K200-K209", label: "Chirurgie · K200-K209", tarif: 16000 },
      { code: "K210-K219", label: "Chirurgie · K210-K219", tarif: 16800 },
      { code: "K220-K229", label: "Chirurgie · K220-K229", tarif: 17600 },
      { code: "K230-K239", label: "Chirurgie · K230-K239", tarif: 18400 },
      { code: "K240-K249", label: "Chirurgie · K240-K249", tarif: 19200 },
      { code: "K250-K259", label: "Chirurgie · K250-K259", tarif: 20000 },
      { code: "K260-K269", label: "Chirurgie · K260-K269", tarif: 20800 },
      { code: "K270-K279", label: "Chirurgie · K270-K279", tarif: 21600 },
      { code: "K280-K289", label: "Chirurgie · K280-K289", tarif: 22400 },
      { code: "K290-K299", label: "Chirurgie · K290-K299", tarif: 23200 },
      { code: "K300", label: "Chirurgie · K300", tarif: 24000 },
      { code: "K310+", label: "Au-delà de K310 (+800 / 10K)", tarif: 800, unit: "par pas de 10K" },
    ],
  },
  {
    id: "chir-specifiques",
    label: "Chirurgie · Forfaits spécifiques",
    grille: "Grille 2.2",
    scope: "Cataracte, césarienne, amygdalectomie, vésicule",
    unite: "Forfait global / séjour",
    defaultDoctorSpecs: ["Ophtalmologie", "Gynécologie-Obstétrique", "Oto-rhino-laryngologie", "Chirurgie générale"],
    codes: [
      { code: "CAT-S", label: "Cataracte (avec ou sans implant)", tarif: 6500 },
      { code: "CAT-GLA", label: "Cataracte + glaucome", tarif: 10000 },
      { code: "CESAR", label: "Césarienne", tarif: 8000 },
      { code: "VESIC", label: "Vésicule (classique ou coelio)", tarif: 7500 },
      { code: "AMY-AD", label: "Amygdalectomie (adulte)", tarif: 3000 },
      { code: "AMY-EN", label: "Amygdalectomie + adénoïdectomie (enfant)", tarif: 2400 },
    ],
  },
  {
    id: "hospit-medicale",
    label: "Hospitalisation médicale",
    grille: "Grille 3",
    scope: "Médecine, réanimation, soins intensifs, accouchement",
    unite: "Forfait journalier / global",
    defaultDoctorSpecs: ["Médecine interne", "Anesthésie-Réanimation", "Gynécologie-Obstétrique"],
    codes: [
      { code: "HOSP-MED", label: "Hospitalisation en médecine", tarif: 550, unit: "par jour" },
      { code: "HOSP-REA", label: "Hospitalisation en réanimation", tarif: 1500, unit: "par jour" },
      { code: "SOIN-INT", label: "Soins intensifs", tarif: 1000, unit: "par jour" },
      { code: "ACC-MONO", label: "Accouchement mono-fœtal (voie basse)", tarif: 3000, unit: "forfait" },
      { code: "ACC-MULTI", label: "Accouchement multiple", tarif: 3500, unit: "forfait" },
    ],
  },
  {
    id: "actes-exploration",
    label: "Exploration & Imagerie",
    grille: "Grille 4",
    scope: "Endoscopies, scanner, IRM, échographie, laser",
    unite: "Forfait global",
    defaultDoctorSpecs: ["Radiologie", "Gastro-entérologie", "Pneumophtysiologie"],
    codes: [
      { code: "FIBRO-OGD-C", label: "Fibroscopie œso-gastro-duodénale (clinique)", tarif: 1000 },
      { code: "FIBRO-OGD-X", label: "Fibroscopie œso-gastro-duodénale (cabinet)", tarif: 800 },
      { code: "BRONCHO-C", label: "Bronchoscopie (clinique)", tarif: 1000 },
      { code: "BRONCHO-X", label: "Bronchoscopie (cabinet)", tarif: 800 },
      { code: "COLONO", label: "Colonoscopie (± biopsie)", tarif: 1200 },
      { code: "ANGIO-RET", label: "Angiographie rétinienne", tarif: 500 },
      { code: "ANGIO-SCAN", label: "Angio-scanner", tarif: 800 },
      { code: "PHOTO-NN", label: "Photothérapie (nouveau-né)", tarif: 160 },
      { code: "ECHO", label: "Échographie", tarif: 200 },
      { code: "IRM", label: "IRM (avec produit de contraste)", tarif: 2200 },
      { code: "TDM", label: "TDM (avec produit de contraste)", tarif: 1000 },
      { code: "LASER", label: "Laser (par séance)", tarif: 800, unit: "par séance" },
    ],
  },
  {
    id: "chimio",
    label: "Oncologie · Chimiothérapie",
    grille: "Grille 5",
    scope: "Séance de chimiothérapie",
    unite: "Forfait par séance",
    defaultDoctorSpecs: ["Oncologie Médicale", "Hématologie clinique"],
    codes: [
      { code: "CHIMIO-S", label: "Chimiothérapie — séance", tarif: 1000, unit: "par séance" },
    ],
  },
  {
    id: "lithotripsie",
    label: "Urologie · Lithotripsie",
    grille: "Grille 7",
    scope: "Lithotripsie extra-corporelle",
    unite: "Forfait global (jusqu'à guérison)",
    defaultDoctorSpecs: ["Urologie"],
    codes: [
      { code: "LITHO-EC", label: "Lithotripsie extra-corporelle", tarif: 5000 },
    ],
  },
  {
    id: "explo-cardiaque",
    label: "Cardiologie · Exploration",
    grille: "Grille 8",
    scope: "Coronarographie, cathétérisme, échos, Holter",
    unite: "Forfait global",
    defaultDoctorSpecs: ["Cardiologie"],
    codes: [
      { code: "CX01", label: "Coronarographie + ventriculographie gauche", tarif: 6000 },
      { code: "CX02", label: "Aortographie abdo/thora/rénale/cérébrale/pulmonaire", tarif: 3500 },
      { code: "CX03", label: "Artériographie membres sup. / carotidienne", tarif: 3500 },
      { code: "CX04", label: "Artériographie membres inférieurs", tarif: 3500 },
      { code: "CX05", label: "Cathétérisme cardiaque (D ou G)", tarif: 2500 },
      { code: "CX06", label: "Cathétérisme cardiaque (D et G)", tarif: 3000 },
      { code: "CX07", label: "Biopsie myocardique", tarif: 5000 },
      { code: "CX08", label: "Exploration faisceau de His", tarif: 7500 },
      { code: "CX11", label: "Échographie de stress", tarif: 1300 },
      { code: "CX12", label: "Échographie trans-œsophagienne", tarif: 1000 },
      { code: "CX13", label: "Écho-doppler cardiaque", tarif: 650 },
      { code: "CX14", label: "Écho-doppler artériel périphérique", tarif: 600 },
      { code: "CX15", label: "Écho-doppler veineux périphérique", tarif: 600 },
      { code: "CX16", label: "Échographie des artères rénales", tarif: 600 },
      { code: "CX17", label: "Écho-doppler cervical", tarif: 600 },
      { code: "CX18", label: "Épreuve d'effort", tarif: 650 },
      { code: "CX19", label: "Holter ECG", tarif: 600 },
      { code: "CX20", label: "Holter tensionnel", tarif: 500 },
      { code: "CX21", label: "Tilt test", tarif: 400 },
      { code: "CX22", label: "Recherche des potentiels tardifs", tarif: 400 },
      { code: "CX23", label: "Exploration du système nerveux autonome", tarif: 800 },
    ],
  },
  {
    id: "cardio-interv",
    label: "Cardiologie interventionnelle",
    grille: "Grille 9 / 9 bis",
    scope: "Angioplasties, stents, pacemakers, fermetures percutanées",
    unite: "Forfait global",
    defaultDoctorSpecs: ["Cardiologie"],
    codes: [
      { code: "CV01", label: "Sonde de stimulation", tarif: 2500 },
      { code: "CV02", label: "Stimulation œsophagienne", tarif: 550 },
      { code: "CV03", label: "Thrombolyse", tarif: 3000 },
      { code: "CV09", label: "Ballon de contre-pulsion intra-aortique + pose", tarif: 19000 },
      { code: "CV11", label: "Dilatation 1 artère coronaire sans stent", tarif: 20000 },
      { code: "CV12", label: "Dilatation 2 artères coronaires sans stent", tarif: 22500 },
      { code: "CV13", label: "Dilatation 3 artères coronaires sans stent", tarif: 25000 },
      { code: "CV21", label: "Dilatation + 1 stent nu", tarif: 30000 },
      { code: "CV22", label: "Dilatation + 2 stents nus", tarif: 38000 },
      { code: "CV31", label: "Dilatation + 1 stent actif", tarif: 49000 },
      { code: "CV42", label: "Dilatation + 1 stent actif + 1 stent nu", tarif: 57000 },
      { code: "CV51", label: "Angioplastie haut risque + GP IIb/IIIa + 1 stent nu", tarif: 32500 },
      { code: "CV52", label: "Angioplastie haut risque + GP IIb/IIIa + 2 stents nus", tarif: 40500 },
      { code: "CV53", label: "Angioplastie haut risque + GP IIb/IIIa + 1 stent actif", tarif: 51500 },
      { code: "CV61", label: "Dilatation artère(s) périphérique(s) sans stent", tarif: 11000 },
      { code: "CV71", label: "Dilatation périphérique + 1 stent", tarif: 19500 },
      { code: "CV72", label: "Dilatation périphérique + 2 stents", tarif: 27000 },
      { code: "CV81", label: "Dilatation valvulaire mitrale", tarif: 23000 },
      { code: "CV82", label: "Dilatation valvulaire pulmonaire", tarif: 12000 },
      { code: "CV83", label: "Dilatation valvulaire tricuspide", tarif: 10000 },
      { code: "CV84", label: "Dilatation valvulaire aortique", tarif: 12000 },
      { code: "CV90", label: "Septostomie de Rashkind", tarif: 13000 },
      { code: "CV91", label: "Pace maker mono-chambre", tarif: 25000 },
      { code: "CV92", label: "Pace maker double chambre", tarif: 35000 },
      { code: "CV93", label: "Fermeture percutanée canal artériel", tarif: 25000 },
      { code: "CV94", label: "Fermeture percutanée CIA / CIV", tarif: 50000 },
    ],
  },
  {
    id: "chir-cardio-ouv",
    label: "Chirurgie cardio-vasculaire ouverte",
    grille: "Grille 10",
    scope: "Cardiopathies sous CEC — séjour 14 jours forfaitaire",
    unite: "Forfait global (14 jours)",
    defaultDoctorSpecs: ["Chirurgie cardio-vasculaire"],
    codes: [
      { code: "CO00", label: "Cardiopathies simples avec CEC (commissurotomie, mono-pontage)", tarif: 70000 },
      { code: "CO01", label: "Cardiopathies simples/intermédiaires sous CEC (double pontage, CIA)", tarif: 80000 },
      { code: "CO02", label: "Cardiopathies majeures sous CEC (mono RVA, Fallot, triple pontage)", tarif: 110000 },
      { code: "CO03", label: "Cardiopathies compliquées sous CEC (RVA + plastie, pontages multiples)", tarif: 130000 },
      { code: "CO04", label: "Cardiopathies très complexes (Bentall, double RVA + pontages)", tarif: 150000 },
    ],
  },
  {
    id: "chir-cardio-ferm",
    label: "Chirurgie cardio-vasculaire fermée",
    grille: "Grille 11",
    scope: "Interventions à cœur fermé",
    unite: "Forfait global (14 jours)",
    defaultDoctorSpecs: ["Chirurgie cardio-vasculaire"],
    codes: [
      { code: "CF01", label: "Cœur fermé simples (commissurotomie, coarctation, canal artériel)", tarif: 35000 },
      { code: "CF02", label: "Cœur fermé majeures (Blalock-Taussig, Blalock-Hanlon)", tarif: 40000 },
      { code: "CF03", label: "Cœur fermé complexes (doubles/triples actes, péricardectomie)", tarif: 50000 },
    ],
  },
  {
    id: "chambre-impl",
    label: "Chambre implantable",
    grille: "Grille 12",
    scope: "Pose et ablation de chambre implantable",
    unite: "Forfait global",
    defaultDoctorSpecs: ["Oncologie Médicale", "Chirurgie générale"],
    codes: [
      { code: "CHAMB-PA", label: "Chambre implantable (pose + ablation)", tarif: 7000 },
    ],
  },
  {
    id: "radiotherapie",
    label: "Radiothérapie",
    grille: "Grille 13",
    scope: "Forfaits par localisation / organe",
    unite: "Forfait par organe",
    defaultDoctorSpecs: ["Radiothérapie", "Oncologie Médicale"],
    codes: [
      { code: "RT-PROS-NC", label: "Prostate (non conformationnelle)", tarif: 26150 },
      { code: "RT-PROS-C", label: "Prostate (conformationnelle)", tarif: 30600 },
      { code: "RT-SEIN-CONS", label: "Sein — conservateur", tarif: 25200 },
      { code: "RT-SEIN-POST", label: "Sein — post-opératoire", tarif: 20000 },
      { code: "RT-ORL", label: "ORL", tarif: 21600 },
      { code: "RT-ORBITE", label: "Orbite", tarif: 14800 },
      { code: "RT-CAVUM", label: "Cavum", tarif: 27500 },
      { code: "RT-CERVL", label: "Tumeur cérébrale / leucémie", tarif: 19480 },
      { code: "RT-META", label: "Métastases", tarif: 13500 },
      { code: "RT-TEST", label: "Testicule (loc)", tarif: 32000 },
      { code: "RT-SOUS", label: "RT sous-diaphragme", tarif: 32000 },
      { code: "RT-SUS", label: "RT sus-diaphragme", tarif: 32000 },
      { code: "RT-POUM", label: "Poumon / œsophage", tarif: 24000 },
      { code: "RT-ABD", label: "Abdomen / pelvis", tarif: 22770 },
      { code: "RT-VULVE", label: "Vulve / canal anal", tarif: 24180 },
      { code: "RT-NEVRAX", label: "Névraxe", tarif: 30230 },
      { code: "RT-CUTAN", label: "T. cutanée", tarif: 12200 },
      { code: "RT-CASTR", label: "Castration", tarif: 6730 },
      { code: "RT-OS", label: "Os / parties molles", tarif: 25000 },
    ],
  },
  {
    id: "curietherapie",
    label: "Curiethérapie",
    grille: "Grille 15",
    scope: "Curiethérapie à bas débit",
    unite: "Forfait par cure",
    defaultDoctorSpecs: ["Radiothérapie", "Oncologie Médicale"],
    codes: [
      { code: "CURIE-BD", label: "Curiethérapie à bas débit", tarif: 12000 },
    ],
  },
  {
    id: "radiofreq",
    label: "Radiofréquence",
    grille: "Grille 16",
    scope: "Forfait de radiofréquence (sonde incluse)",
    unite: "Forfait par séance",
    defaultDoctorSpecs: ["Cardiologie", "Oncologie Médicale", "Radiologie"],
    codes: [
      { code: "RF-SEANCE", label: "Radiofréquence — séance", tarif: 15000 },
    ],
  },
  {
    id: "iratherapie",
    label: "Médecine nucléaire · Irathérapie",
    grille: "Grille 17",
    scope: "Iode 131 — hospitalisation + déchets",
    unite: "Forfait par séance",
    defaultDoctorSpecs: ["Médecine nucléaire"],
    codes: [
      { code: "IRA-131", label: "Irathérapie (Iode 131)", tarif: 27000 },
    ],
  },
  {
    id: "greffe-mo",
    label: "Hématologie · Greffe moelle",
    grille: "Grille 18",
    scope: "Greffe de moelle osseuse — 30 jours",
    unite: "Forfait global (30 jours)",
    defaultDoctorSpecs: ["Hématologie clinique"],
    codes: [
      { code: "GMO-30", label: "Greffe moelle osseuse (30 j hosp. stérile)", tarif: 280000 },
      { code: "BIOPS-OM", label: "Biopsie ostéo-médullaire (Grille 20)", tarif: 800 },
    ],
  },
  {
    id: "hospit-sterile",
    label: "Hospitalisation chambre stérile",
    grille: "Grille 19",
    scope: "Forfait journalier en chambre stérile",
    unite: "Forfait par jour",
    defaultDoctorSpecs: ["Hématologie clinique", "Oncologie Médicale"],
    codes: [
      { code: "HOSP-ST", label: "Hospitalisation chambre stérile", tarif: 1500, unit: "par jour" },
    ],
  },
];

// ---- assignment store (per doctor) -----------------------------------------

// localStorage key (not a secret)
const KEY = "dikdoc.ngap.assignments";

export type AssignmentMap = Record<string, string[]>; // doctorId -> [codeId...]

export function loadAssignments(): AssignmentMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as AssignmentMap) : {};
  } catch {
    return {};
  }
}

export function saveAssignments(map: AssignmentMap) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export const DOCTOR_DIRECTORY: { id: string; name: string; speciality: string }[] = [
  { id: "1", name: "Dr Sam Frank", speciality: "Cardiologie" },
  { id: "2", name: "Dr Nikki Qlay", speciality: "Dermatologie" },
  { id: "3", name: "Dr Emily Smith", speciality: "Pédiatrie" },
  { id: "4", name: "Dr Dan Johnson", speciality: "Médecine interne" },
  { id: "5", name: "Dr Sarah Davis", speciality: "Gynécologie-Obstétrique" },
  { id: "william", name: "Dr William Tennessee", speciality: "Néphrologie" },
];
